import {
  FileCheck,
  FilePen,
  FilePlus2,
  HelpCircle,
  Images,
  Languages,
  ListChecks,
  PenLine,
  SearchX,
  Send,
} from "lucide-react";
import type { ComponentType } from "react";

import { cn } from "@/lib/utils";

import type { EscalationKind, Outcome, Tier } from "./types";

interface OutcomeMeta {
  label: string;
  /** What the composer is doing about it, in the requestor's language. */
  blurb: string;
  icon: ComponentType<{ className?: string }>;
  accent: string;
}

// The five things the composer can honestly do. The requestor never picks one
// of these — it reports which one their request landed in, and why. Keeping the
// wording identical everywhere is what makes the model learnable.
const OUTCOME_META: Record<Outcome, OutcomeMeta> = {
  asset_as_is: {
    label: "Approved asset found",
    blurb: "Nothing generated — an existing asset already answers this",
    icon: FileCheck,
    accent: "text-outcome-applied bg-outcome-applied-tint border-outcome-applied/20",
  },
  generate_deck: {
    label: "Deck generated",
    blurb: "Built on the VELUX template and translated — fully editable",
    icon: FilePlus2,
    accent: "text-velux bg-velux-tint border-velux/20",
  },
  generate_social_ads: {
    label: "Social ads generated",
    blurb: "An approved photo, laid out on brand — one file per format you chose",
    icon: Images,
    accent: "text-velux bg-velux-tint border-velux/20",
  },
  localize_document: {
    label: "Translated in place",
    blurb: "Your file, same layout and images — only the text changed",
    icon: Languages,
    accent: "text-velux-blue bg-velux-blue-tint border-velux-blue/25",
  },
  propose_pdf_pages: {
    label: "Your choice needed",
    blurb: "Fixed layout — pick the pages to keep and they get translated in place",
    icon: ListChecks,
    accent: "text-velux-blue bg-velux-blue-tint border-velux-blue/25",
  },
  revise_deck: {
    label: "Deck edited",
    blurb: "Only the slides you named changed — every other slide is as it was built",
    icon: FilePen,
    accent: "text-velux bg-velux-tint border-velux/20",
  },
  write_copy: {
    label: "Copy written",
    blurb: "The words themselves — copy them out, or take the Word file to forward",
    icon: PenLine,
    accent: "text-velux bg-velux-tint border-velux/20",
  },
  escalate_source_edit: {
    label: "Needs a manual touch",
    blurb: "Not attempted — the request to Strategic Comms is drafted below",
    icon: Send,
    accent: "text-outcome-escalated bg-outcome-escalated-tint border-outcome-escalated/25",
  },
  needs_clarification: {
    label: "Which asset?",
    blurb: "The request names a change but nothing to apply it to",
    icon: HelpCircle,
    accent: "text-muted bg-surface-muted border-border",
  },
};

const MISSING_ASSET_META: OutcomeMeta = {
  label: "Not in the library",
  blurb: "Nothing approved answers this — the request to Strategic Comms is drafted below",
  icon: SearchX,
  accent: "text-outcome-escalated bg-outcome-escalated-tint border-outcome-escalated/25",
};

// A revision that resolved no edit it could act on. It is a question, not a
// result — and labelling it "Deck edited" would claim a change that was never
// made, which is the exact failure this whole route exists to end.
const UNRESOLVED_REVISION_META: OutcomeMeta = {
  label: "Which slide?",
  blurb: "Nothing was changed — name the slide by number and I'll edit just that one",
  icon: HelpCircle,
  accent: "text-muted bg-surface-muted border-border",
};

interface OutcomeBannerProps {
  outcome: Outcome;
  tier: Tier;
  /** True while a deck or a set of ads has been planned but not yet built. */
  awaitingBuild?: boolean;
  /** Which tier-3 ask this is, when the outcome is an escalation. */
  escalationKind?: EscalationKind;
  /** True when a deck revision named no slide the composer could act on. */
  revisionUnresolved?: boolean;
  /** True when the clarification carries ready-to-send options to choose from. */
  hasClarificationChoices?: boolean;
}

// An ambiguous request, not a missing one. "Which asset?" describes the other
// clarification — a change with nothing to apply it to — and claiming the
// library came up short when two readings both work would send a market
// looking for a file that is not the problem.
const AMBIGUOUS_REQUEST_META: OutcomeMeta = {
  label: "Which did you mean?",
  blurb: "The request reads two ways — pick one and I'll run it",
  icon: HelpCircle,
  accent: "text-muted bg-surface-muted border-border",
};

/** The decided outcome, shown as the headline of an assistant turn. */
export function OutcomeBanner({
  outcome,
  tier,
  awaitingBuild = false,
  escalationKind,
  revisionUnresolved = false,
  hasClarificationChoices = false,
}: OutcomeBannerProps) {
  const base =
    outcome === "needs_clarification" && hasClarificationChoices
      ? AMBIGUOUS_REQUEST_META
      : outcome === "revise_deck" && revisionUnresolved
        ? UNRESOLVED_REVISION_META
        : // Both escalations are tier 3 and both end in a brief, but "we don't
          // have this" and "this needs the source file" are different answers,
          // and a banner that gave the second for the first read as a bug.
          outcome === "escalate_source_edit" && escalationKind === "missing_asset"
          ? MISSING_ASSET_META
          : (OUTCOME_META[outcome] ?? OUTCOME_META.generate_deck);
  // A deck turn — and a social-ad turn — now stops at a plan, so the banner
  // must not claim a file exists before one does. The tier system's credibility
  // rests on never overstating what actually happened.
  const meta =
    awaitingBuild && outcome === "generate_deck"
      ? {
          ...base,
          label: "Ready to build",
          blurb: "Planned on the VELUX template — nothing is written until you approve",
        }
      : awaitingBuild && outcome === "generate_social_ads"
        ? {
            ...base,
            label: "Your choice needed",
            blurb: "Photo and copy are chosen — pick the formats and I'll render them",
          }
        : awaitingBuild && outcome === "revise_deck"
          ? {
              ...base,
              label: "Ready to edit",
              blurb: "These changes only — the deck is not rebuilt until you approve",
            }
          : base;
  const Icon = meta.icon;

  return (
    <div className={cn("flex items-start gap-3 rounded-xl border px-3 py-2.5", meta.accent)}>
      <Icon className="mt-0.5 h-4 w-4 shrink-0" />
      <div className="min-w-0">
        <p className="text-sm font-semibold">
          {meta.label}
          <span className="ml-2 font-normal opacity-70">Tier {tier}</span>
        </p>
        <p className="text-xs opacity-80">{meta.blurb}</p>
      </div>
    </div>
  );
}

export { OUTCOME_META };
